import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, articles } from '@/mocks/games';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'newest' | 'rating'>('newest');
  const [activeFilter, setActiveFilter] = useState(slug || 'all');

  const currentCategory = categories.find((c) => c.slug === slug);

  const filteredArticles = useMemo(() => {
    let filtered = activeFilter === 'all'
      ? articles
      : articles.filter((a) => a.category === activeFilter);

    if (sortBy === 'rating') {
      filtered = [...filtered].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    } else {
      filtered = [...filtered].sort((a, b) => b.date.localeCompare(a.date));
    }

    return filtered;
  }, [activeFilter, sortBy]);

  if (!currentCategory && slug) {
    return (
      <>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">
              分类未找到
            </h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const pageTitle = currentCategory
    ? `${currentCategory.name}工口游戏推荐与攻略 - 工口游戏攻略大全`
    : '全部工口游戏推荐与攻略 - 工口游戏攻略大全';

  const pageDesc = currentCategory
    ? `${currentCategory.description}。浏览${currentCategory.count}款${currentCategory.name}工口游戏的详细攻略与评测。`
    : '浏览全部工口游戏分类，涵盖RPG、AVG、SLG、ACT等多种类型，找到最适合你的工口游戏攻略与推荐。';

  const seoData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDesc,
    url: `https://eroge-guide.com/category/${slug || ''}`,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`工口游戏,${currentCategory?.name || '工口游戏推荐'},工口游戏攻略,工口游戏排行榜`}
        canonicalUrl={`https://eroge-guide.com/category/${slug || ''}`}
        ogType="website"
        schemaJson={seoData}
      />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory?.name || '全部游戏' },
            ]}
          />

          {/* Category Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className={`${currentCategory?.icon || 'ri-gamepad-line'} text-2xl`}></i>
              </span>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {currentCategory ? currentCategory.name : '全部游戏'}
                </h1>
                {currentCategory && (
                  <p className="text-sm text-foreground-500 mt-0.5">
                    共 {currentCategory.count} 款游戏
                  </p>
                )}
              </div>
            </div>
            <p className="text-foreground-600 leading-relaxed">
              {currentCategory?.description || '浏览全部工口游戏分类'}
            </p>
          </div>

          {/* Filter Bar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 p-4 bg-background-50 border border-background-200/60 rounded-lg">
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={() => setActiveFilter('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  activeFilter === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveFilter(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    activeFilter === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name.split(' ')[0]}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-foreground-400">排序：</span>
              <button
                onClick={() => setSortBy('newest')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  sortBy === 'newest'
                    ? 'bg-secondary-500 text-background-50 dark:text-foreground-950'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                最新发布
              </button>
              <button
                onClick={() => setSortBy('rating')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  sortBy === 'rating'
                    ? 'bg-secondary-500 text-background-50 dark:text-foreground-950'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                评分最高
              </button>
            </div>
          </div>

          {/* Articles */}
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 mb-14">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.slug} {...article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}